import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { ProOnlyPage } from '@/components/pro-only-page'
import { apiV2, type V2NetworkEntry } from '@/lib/api-v2-client'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog'
import { StatusBadge } from '@/components/status-badge'
import { MetricGrid } from '@/components/metric-tile'
import { DisclosurePanel } from '@/components/disclosure-panel'
import { LoadingState, ErrorState } from '@/components/state-view'
import { IgnoreRegionsEditor } from '@/components/ignore-regions-editor'
import { BrowserRunReplay } from '@/components/browser-run-replay'
import { WebVitals } from '@/components/web-vitals'
import type { Region } from '@/lib/ignore-regions'
import { locationByCode } from '@/lib/locations'

export const Route = createFileRoute('/_authenticated/dashboard/monitors/$id_/browser-runs_/$runId')({
  component: () => (
    <ProOnlyPage
      title="Browser runs are on Pro"
      text="Browser checks, their run history and screenshots need a paid plan."
    >
      <BrowserRunDetailPage />
    </ProOnlyPage>
  ),
})

function BrowserRunDetailPage() {
  const { id, runId } = Route.useParams()
  const queryClient = useQueryClient()
  const [view, setView] = useState<'current' | 'baseline' | 'diff'>('current')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['browser-run', id, runId],
    queryFn: () => apiV2.getBrowserRun(id, runId),
  })
  const { data: monitorResp } = useQuery({
    queryKey: ['monitor', id],
    queryFn: () => apiV2.getMonitor(id),
  })

  const acceptBaseline = useMutation({
    mutationFn: () => {
      const settings = (monitorResp?.data?.settings ?? {}) as Record<string, unknown>
      const current = typeof settings.visual_baseline_version === 'number' ? settings.visual_baseline_version : 0
      return apiV2.updateMonitor(id, { settings: { ...settings, visual_baseline_version: current + 1 } })
    },
    onSuccess: () => {
      toast.success('Baseline reset — the next successful run becomes the new baseline')
      queryClient.invalidateQueries({ queryKey: ['monitor', id] })
    },
    onError: () => toast.error('Failed to reset baseline'),
  })

  const settingsObj = (monitorResp?.data?.settings ?? {}) as Record<string, unknown>
  const savedRegions: Region[] = Array.isArray(settingsObj.visual_ignore_regions)
    ? (settingsObj.visual_ignore_regions as Region[])
    : []
  const [regionsOpen, setRegionsOpen] = useState(false)
  const [draftRegions, setDraftRegions] = useState<Region[]>([])
  const saveRegions = useMutation({
    mutationFn: () => apiV2.updateMonitor(id, { settings: { ...settingsObj, visual_ignore_regions: draftRegions } }),
    onSuccess: () => {
      toast.success('Ignore regions saved')
      setRegionsOpen(false)
      queryClient.invalidateQueries({ queryKey: ['monitor', id] })
    },
    onError: () => toast.error('Failed to save ignore regions'),
  })

  if (isLoading) return <LoadingState rows={6} />
  if (isError || !data) return <ErrorState message="Couldn't load this run." onRetry={() => refetch()} />

  const run = data.data
  const isRegression = run.visual_passed === false
  const hasVisual = run.ssim != null || run.baseline_url != null

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ label: 'All runs', to: '/dashboard/monitors/$id/browser-runs', params: { id } }}
        title={<>Run <span className="font-mono">{run.check_run_id.slice(0, 8)}</span></>}
        badges={isRegression ? <StatusBadge kind="degraded" label="Visual change detected" /> : <StatusBadge status={run.status} />}
      />

      <MetricGrid
        aria-label="Run summary"
        items={[
          { label: 'Duration', value: `${run.duration_ms.toLocaleString()} ms` },
          {
            label: 'Steps',
            value: String(run.step_count),
            sublabel: run.failed_step_index != null ? `failed at step ${run.failed_step_index + 1}` : 'all passed',
            tone: run.failed_step_index != null ? 'down' : undefined,
          },
          ...(run.ssim != null
            ? [{ label: 'Visual match (SSIM)', value: run.ssim.toFixed(3), sublabel: isRegression ? 'below threshold' : 'within threshold' }]
            : []),
          { label: 'Region', value: run.region ? (locationByCode(run.region)?.city ?? run.region) : '—', mono: false },
        ]}
      />

      {run.error && (
        <div role="alert" className="rounded-lg border border-status-down bg-status-down-soft px-4 py-3 text-sm text-status-down">
          {run.error}
        </div>
      )}

      <WebVitals vitals={run} />

      {/* Stepped timeline — the failing step opens by default. Per-step
          console/HAR/DOM live in the tarball (download below). */}
      {run.steps && run.steps.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">Steps</h2>
          {run.steps.map((s) => (
            <DisclosurePanel
              key={s.index}
              defaultOpen={!s.ok}
              title={
                <span className="flex items-center gap-2">
                  <StatusBadge kind={s.ok ? 'up' : 'down'} label={`Step ${s.index + 1}`} />
                  <span className="truncate">{s.name}</span>
                </span>
              }
              right={<span className="text-xs tabular-nums text-muted-foreground">{s.duration_ms.toLocaleString()} ms</span>}
            >
              {s.error ? (
                <pre tabIndex={0} className="overflow-x-auto whitespace-pre-wrap text-xs text-destructive">{s.error}</pre>
              ) : (
                <p className="text-sm text-muted-foreground">Step passed.</p>
              )}
            </DisclosurePanel>
          ))}
        </div>
      )}

      {/* Console + network (93-surface-browser-run-diagnostics) — structured, no
          tarball download needed. Empty for pre-diagnostics runs. */}
      {run.console && run.console.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">Console ({run.console.length})</h2>
          <div className="divide-y rounded border">
            {run.console.map((c, i) => (
              <div key={i} className="flex gap-2 px-3 py-1.5 font-mono text-xs">
                <span className={`shrink-0 uppercase ${c.level === 'error' ? 'text-destructive' : c.level === 'warning' ? 'text-status-degraded' : 'text-muted-foreground'}`}>
                  {c.level}
                </span>
                <span className="break-all">{c.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {run.network && run.network.length > 0 && <NetworkPanel network={run.network} />}

      {/* Visual comparison — final-state current vs baseline (+ diff overlay on a
          regression). Per-step screenshots live in the replay below. */}
      {hasVisual && (
        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="inline-flex rounded-md border border-border p-0.5 text-sm">
              <button
                className={`rounded px-3 py-1 ${view === 'current' ? 'bg-secondary font-medium' : 'text-muted-foreground'}`}
                onClick={() => setView('current')}
              >
                Current
              </button>
              <button
                className={`rounded px-3 py-1 ${view === 'baseline' ? 'bg-secondary font-medium' : 'text-muted-foreground'}`}
                onClick={() => setView('baseline')}
                disabled={!run.baseline_url}
              >
                Baseline
              </button>
              {run.diff_url && (
                <button
                  className={`rounded px-3 py-1 ${view === 'diff' ? 'bg-status-degraded-soft font-medium text-status-degraded' : 'text-muted-foreground'}`}
                  onClick={() => setView('diff')}
                >
                  Diff
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(run.baseline_url || run.screenshot_url) && (
                <Dialog
                  open={regionsOpen}
                  onOpenChange={(o) => {
                    setRegionsOpen(o)
                    if (o) setDraftRegions(savedRegions)
                  }}
                >
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      Edit ignore regions
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>Ignore regions</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                      Drag to mask areas that change between runs (clocks, carousels, ads). They're
                      excluded from the visual comparison.
                    </p>
                    <IgnoreRegionsEditor
                      imageUrl={(run.baseline_url ?? run.screenshot_url) as string}
                      value={draftRegions}
                      onChange={setDraftRegions}
                    />
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setRegionsOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={() => saveRegions.mutate()} disabled={saveRegions.isPending}>
                        Save regions
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              {isRegression && (
                <Button size="sm" variant="outline" onClick={() => acceptBaseline.mutate()} disabled={acceptBaseline.isPending}>
                  Accept new baseline
                </Button>
              )}
            </div>
          </div>
          {(() => {
            const url =
              view === 'current'
                ? run.screenshot_url
                : view === 'baseline'
                  ? run.baseline_url
                  : run.diff_url
            const label = view === 'diff' ? 'difference overlay' : `${view} screenshot`
            return url ? (
              <>
                {view === 'diff' && (
                  <p className="text-sm text-status-degraded">
                    Highlighted areas changed since the baseline.
                  </p>
                )}
                <img src={url} alt={label} className="w-full rounded border border-border" />
              </>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">No {label} available.</p>
            )
          })()}
        </div>
      )}

      <BrowserRunReplay tarballUrl={run.tarball_url} steps={run.steps} />

      {(run.tarball_url || run.trace_url || run.video_url) && (
        <div className="space-y-1 text-sm">
          {run.tarball_url && (
            <div>
              <a href={run.tarball_url} className="text-primary underline" target="_blank" rel="noreferrer">
                Download full artifacts (.tar.gz)
              </a>
              <span className="text-muted-foreground"> — per-step screenshots, console, network (HAR), DOM.</span>
            </div>
          )}
          {run.trace_url && (
            <div>
              <a href={run.trace_url} className="text-primary underline" target="_blank" rel="noreferrer">
                Download Playwright trace (.zip)
              </a>
              <span className="text-muted-foreground"> — open at trace.playwright.dev to step through the run.</span>
            </div>
          )}
          {run.video_url && (
            <div>
              <a href={run.video_url} className="text-primary underline" target="_blank" rel="noreferrer">
                Download failure video (.webm)
              </a>
              <span className="text-muted-foreground"> — recorded because this run failed.</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Network requests for a run — filter by type, 4xx/5xx flagged, count summary. */
function NetworkPanel({ network }: { network: V2NetworkEntry[] }) {
  const [type, setType] = useState<string>('all')
  const types = ['all', ...Array.from(new Set(network.map((n) => n.type).filter(Boolean)))]
  const rows = type === 'all' ? network : network.filter((n) => n.type === type)
  const failed = network.filter((n) => n.status >= 400).length
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-muted-foreground">
          Network ({network.length}){failed > 0 && <span className="text-destructive"> · {failed} failed</span>}
        </h2>
        <div className="flex flex-wrap gap-1">
          {types.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`rounded px-2 py-0.5 text-xs ${type === t ? 'bg-secondary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded border">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b">
              <th className="px-2 py-1 text-left font-medium">URL</th>
              <th className="px-2 py-1 text-left font-medium">Type</th>
              <th className="px-2 py-1 text-right font-medium">Status</th>
              <th className="px-2 py-1 text-right font-medium">Size</th>
              <th className="px-2 py-1 text-right font-medium">Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="max-w-[28rem] truncate px-2 py-1 font-mono" title={n.url}>{n.url}</td>
                <td className="px-2 py-1 text-muted-foreground">{n.type || '—'}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${n.status >= 400 ? 'font-medium text-destructive' : ''}`}>{n.status || '—'}</td>
                <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">{n.size ? `${(n.size / 1024).toFixed(1)} kB` : '—'}</td>
                <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">{n.duration_ms ? `${n.duration_ms} ms` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
